/**
 * PuzzleTagField
 * Data prop is array of items <object> that populates dropdown list of TagField
 * Besides 'data', component is expecting two main props: valueField <string> and textField <string>
 *  valueField - name of property in item object that contains unique 'key' of that item (that 'key' (item[valueField]) is pushed into TagField.value array when selected)
 *  textField - name of property in item object that contains display value in dropdown list
 *
 * If you need item.textField to be translated, pass 'this.translate' function to 'translate' prop and
 * in localisation add the object (named same as the 'name' prop of TagField) with keys that corresponds to 'textField's of items
 */

import React from 'react';
import { PureComponent } from '../../../dispatcher/modules';
import { FIELD_OMITTED_PROPS } from '../../../utility';
import Field from '../../layout/field/Field';
import {
  PUZZLE_TAG_FIELD_PROPS,
  PUZZLE_TAG_FIELD_STATE
} from './Types';
import {
  MultiSelect,
  MultiSelectBlurEvent,
  MultiSelectChangeEvent,
  MultiSelectFilterChangeEvent,
  MultiSelectFocusEvent,
  MultiSelectPageChangeEvent,
  ListItemProps,
  TagData
} from '@progress/kendo-react-dropdowns';
import { filterBy } from '@progress/kendo-data-query';
import _, { isArray, isUndefined } from 'lodash';
import classNames from 'classnames';
import {
  MultiSelectCloseEvent,
  MultiSelectOpenEvent
} from '@progress/kendo-react-dropdowns/dist/npm/MultiSelect/MultiSelectProps';
import { Label } from '@progress/kendo-react-labels';

const isPresent = (value: any[]) => value !== null && value !== undefined && value.length;

export default class PuzzleTagField extends PureComponent<PUZZLE_TAG_FIELD_PROPS, PUZZLE_TAG_FIELD_STATE> {
  ariaOwnsId: any;
  component: any;
  translatedData: any;
  inputId = '';
  name: 'puzzletagfield';

  public state: PUZZLE_TAG_FIELD_STATE = {
    initData: null,
    data: null
  };

  static defaultProps = {
    size: 'medium'
  }

  events = {
    onBlur: (event: MultiSelectBlurEvent) => this.triggerEvent('onBlur', event),
    onFocus: (event: MultiSelectFocusEvent) => this.triggerEvent('onFocus', event),
    onChange: (event: MultiSelectChangeEvent) => this.triggerEvent('onChange', event),
    onPageChange: (event: MultiSelectPageChangeEvent) => this.triggerEvent('onPageChange', event)
  };

  get value() {
    let returnValue;
    if (this.component) {
      const { valueField } = this.props;
      const value = this.component.value;

      const { remoteTagField } = this.props;
      if (remoteTagField) {
        returnValue = value;
      } else {
        returnValue = isPresent(value) && valueField ? value.map((option: any) => option[valueField]) : value;
      }
    }

    return returnValue;
  }

  /**
   * Adding aria-required attribute to input - accessibility
   */
  componentDidMount() {
    const { required, id, translate } = this.props;
    const element = this.component._element;
    const tagsHolder = element?.querySelector('div.k-chip-list');
    if (tagsHolder) {
      tagsHolder.setAttribute('aria-label', this.translate('selectedItems'));
    }
    const input = this.component._input;
    this.inputId = id ? id : `selector-${input.id}`;
    this.ariaOwnsId = input?.getAttribute('aria-owns');
    input?.removeAttribute('aria-owns');
    if (required) {
      input?.setAttribute('aria-required', 'true');
    }

    input?.setAttribute('role', 'combobox');
  }

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any) {
    const input = this.component._input;
    let attrValue;
    if (!_.isEqual(prevProps, this.props)) {
      if (input.hasAttribute('aria-describedby')) {
        if (this.props.value?.length) {
          attrValue = input.getAttribute('aria-describedby').trim();
          input.setAttribute('aria-describedby', attrValue);
        } else {
          input.removeAttribute('aria-describedby');
        }
      }
    } else {
      input.removeAttribute('aria-describedby');
    }
  }

  /**
   * Sets props data from component state by setting component state by getDerivedStateFromProps method.
   * @param {PUZZLE_TAG_FIELD_PROPS} props
   * @param {PUZZLE_TAG_FIELD_STATE} state
   */
  static getDerivedStateFromProps(props: PUZZLE_TAG_FIELD_PROPS, state: PUZZLE_TAG_FIELD_STATE) {
    let derivedState = null;
    if (props.data !== state.initData) {
      derivedState = {
        data: props.data,
        initData: props.data
      };
    }
    return derivedState;
  }

  /**
   * Sets filtered itself state
   * @param {MultiSelectFilterChangeEvent} event Event object
   */
  filterChange = (event: MultiSelectFilterChangeEvent) => {
    let { valueField, value, remoteTagField } = this.props;

    if (!remoteTagField) {
      let { initData } = this.state;

      let newData = filterBy(initData, event.filter);
      if (value && value.length && initData.length !== newData.length) {
        let length = value.length;
        let i = 0;
        let current;
        let currentRecord;
        while (i < length) {
          current = value[i];
          if (valueField) {
            currentRecord = _.find(initData, { [valueField]: current });
          } else {
            currentRecord = _.find(initData, current);
          }
          if (!_.includes(newData, currentRecord) && currentRecord) {
            newData.push(currentRecord);
          }
          i++;
        }
      }

      this.setState({
        data: newData
      });
    }

    this.triggerEvent('onFilterChange', event);
  };

  /**
   * Remove aria-owns attribute when dropdown is closed
   * @param event
   */
  onClose = (event: MultiSelectCloseEvent) => {
    const { onClose } = this.props;
    onClose && onClose(event);

    const input = this.component._input;
    input?.removeAttribute('aria-owns');
  };

  /**
   * Adds aria-owns attribute when dropdown is open
   * @param event
   */
  onOpen = (event: MultiSelectOpenEvent) => {
    const { onOpen } = this.props;
    onOpen && onOpen(event);

    const input = this.component._input;
    input?.setAttribute('aria-owns', this.ariaOwnsId);
  };

  /**
   * Added to focus input element of MultiSelect on tag remove
   */
  onTagDelete = (data: any, evt: any) => {
    this.component._input.focus();
    this.component.onTagDelete(data, evt);
  };

  /**
   * Tag render
   * @param data
   * @param tag
   */
  tagRender = (data: TagData, tag: React.ReactElement<any>) => {
    const {
      tagColoring,
      tagRender,
      disabled
    } = this.props;
    let liProps = tag.props;

    if (tagRender) {
      return tagRender(data, React.cloneElement(tag,
        {
          ...liProps,
          disabled: !!disabled,
          tabIndex: 0
        },
        tag.props.children));
    } else if (tagColoring) {
      liProps = tagColoring(data, tag);
    }

    return React.cloneElement(
      tag,
      {
        ...liProps,
        disabled: !!disabled,
        tabIndex: 0,
        removable: !disabled
      },
      tag.props.children
    );
  };

  /**
   * Default itemRender
   * @param li
   * @param itemProps
   */
  itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
    const {
      itemColoring,
      itemRender,
      remoteTagField
    } = this.props;
    if (itemRender) {
      return itemRender(li, itemProps);
    }

    let includes;
    if (remoteTagField) {
      includes = !!_.find(this.props.value, {
        value: itemProps.dataItem.value
      });
    } else {
      if (!isUndefined(this.props.value)) {
        includes = this.props.value?.includes(itemProps.dataItem[this.props.valueField]);
      } else {
        // when is used as uncontrolled component
        let stateValues = _.get(this, 'component.state.value');
        if (isArray(stateValues)) {
          includes = !!_.find(stateValues,
            (item: any) => item[this.props.valueField] === itemProps.dataItem[this.props.valueField]
          );
        }
      }
    }

    let liProps: any = li.props;
    if (includes) {
      liProps = {
        ...liProps,
        className: 'hidden'
      };
    } else if (itemColoring) {
      liProps = itemColoring(li as React.ReactElement<any>, itemProps);
    }

    return React.cloneElement(
      li,
      { ...liProps },
      li.props.children
    );
  };

  render() {
    const {
      data
    } = this.state;

    const {
      ariaDescribedBy,
      asField,
      dataCy,
      defaultValue,
      disabled,
      labelClassName,
      errorTxt,
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl,
      label = '',
      omitAllSelectedItems,
      size,
      showCount,
      translate,
      translateItems,
      valid,
      value,
      valueField,
      withValidation
    } = this.props;
    const restProps = _.omit(this.props,
      ['label',
        'errorTxt',
        'withValidation',
        'required',
        ...FIELD_OMITTED_PROPS]);
    this.translatedData = translate && translateItems ? data.map(this.translateItem.bind(this)) : data;
    const fieldWrapCls = classNames(
      'tp-puzzle-tag-field',
      {
        'k-medium': size === 'medium',
        'k-state-empty': value?.length < 1,
        'k-state-invalid': valid === false,
        'k-state-disabled': disabled
      }
    );

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    // eslint-disable-next-line sonarjs/no-nested-template-literals
    const finalTagLabel = `${label}${showCount && value?.length ? `(${value.length})` : ''}`;

    // removes all selected items from tagField list
    if (omitAllSelectedItems && !!value) {
      if (_.isUndefined(valueField)) {
        this.translatedData = this.translatedData?.filter(item => !value.includes(item));
      } else {
        this.translatedData = this.translatedData?.filter(item => !value.includes(item[valueField]));
      }
    }

    const multiSelectRender = (
      <div
        data-cy={dataCy ? `${dataCy}-wrapper` : undefined}
        className={fieldWrapCls}
      >
        {label && <Label
          editorId={this.inputId}
          className={classNames({
            [labelClassName]: labelClassName,
            'k-mandatory': !asField && fieldMandatory
          })}
        >
          {finalTagLabel}
        </Label>}
        <MultiSelect
          {...restProps}
          id={this.inputId}
          data-cy={dataCy ? dataCy : undefined}
          onClose={this.onClose}
          onOpen={this.onOpen}
          data={this.translatedData}
          value={this.itemFromValue(value)}
          dataItemKey={valueField}
          defaultValue={this.itemFromValue(defaultValue)}
          ref={(component: any) => this.component = component}
          tagRender={this.tagRender}
          itemRender={this.itemRender}
          filterable
          onFilterChange={this.filterChange}
          {...this.events}
          name={this.name}
        />
        {withValidation &&
          <div
            data-cy={dataCy ? `${dataCy}-error-message` : undefined}
            className={'tp-validation-error-message'}
            id={ariaDescribedBy}
          >
            {errorTxt}
          </div>
        }
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {multiSelectRender}
        </Field>
      );
    }

    return multiSelectRender;
  }

  translateItem(item: any[]) {
    const { textField, translate, name } = this.props;
    return {
      ...item,
      [textField]: translate(name + '.' + item[textField])
    };
  }

  /**
   * Helper method for triggering events outside the cmp itself
   * */
  triggerEvent(eventType: string, event: any) {
    if (this.props[eventType]) {
      this.props[eventType].call(undefined, {
        ...event,
        target: this
      });
    }
  }

  itemFromValue(value: any) {
    let returnValue = value;
    const { remoteTagField, omitAllSelectedItems } = this.props;
    if (!remoteTagField) {
      const { translate, translateItems, valueField } = this.props;
      const { data: stateData } = this.state;
      const data = omitAllSelectedItems ? (translate && translateItems ? stateData.map(this.translateItem.bind(this)) : stateData) : this.translatedData;
      returnValue = isPresent(value) && valueField ?
        value.map((optionValueField: any) => data.find((item: any) => item[valueField] === optionValueField))
        : value;
    }

    return returnValue;
  }
}

